import axios from "axios";
import i18n from "@/core/i18n";

/**
 * Wraps a transport/system failure whose message is a raw, untranslated string
 * (e.g. the native BLE layer's "Timeout" or "Writing Characteristic"). Tagging
 * such errors lets us surface the generic message instead of leaking them, since
 * they are otherwise plain `Error` objects indistinguishable from business ones.
 */
export class TechnicalError extends Error {
  constructor(message?: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "TechnicalError";
  }
}

/**
 * Technical errors are runtime/transport failures that leak unreadable strings
 * to the user (e.g. "AxiosError: Network Error", "undefined is not a function").
 * Business errors (API `detail`, BLE messages) are already localized and meant
 * to be shown as-is.
 */
const isTechnicalError = (error: unknown): boolean => {
  // Network failures, timeouts, no HTTP response, etc.
  if (axios.isAxiosError(error)) {
    return true;
  }
  // Failures explicitly tagged at the transport boundary (e.g. native BLE).
  if (error instanceof TechnicalError) {
    return true;
  }
  // Built-in runtime errors always signal a code fault, never a user message.
  return (
    error instanceof TypeError ||
    error instanceof RangeError ||
    error instanceof ReferenceError ||
    error instanceof SyntaxError ||
    error instanceof URIError ||
    error instanceof EvalError
  );
};

/**
 * Returns the message to display for an error: a generic localized message for
 * technical/system errors, the original (already localized) message otherwise.
 */
export const getErrorMessage = (error: unknown): string => {
  if (isTechnicalError(error)) {
    return i18n.global.t("GenericError");
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === "string" && error) {
    return error;
  }
  return i18n.global.t("GenericError");
};
